// One partner report on a run: status, who and when, the message, the photo
// (loaded on demand through a signed URL) and the reviewer's note. The Runs
// page adds its accept / reject controls as children; the Meldingen page
// shows it read-only.
import { useState, type ReactNode } from 'react';
import { getReportPhotoUrl, type RunReport, type RunReportStatus } from '../lib/runs';
import { formatError } from '../lib/errors';
import { formatVenueDateTime } from '../lib/time';

export const REPORT_STATUS_LABEL: Record<RunReportStatus, string> = {
  open:     'Gemeld · wacht op beoordeling',
  accepted: 'Geaccepteerd · niet gefactureerd',
  rejected: 'Afgewezen',
};

const REPORT_STATUS_CLASS: Record<RunReportStatus, string> = {
  open:     'voucher-status-warn',
  accepted: 'voucher-status-ok',
  rejected: 'voucher-status-off',
};

export function RunReportCard({
  report,
  loadPhotoUrl = getReportPhotoUrl,
  children,
}: {
  report: RunReport;
  loadPhotoUrl?: (path: string) => Promise<string>;
  children?: ReactNode;
}) {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  async function togglePhoto() {
    if (!report.photo_path) return;
    if (photoUrl) {
      setPhotoUrl(null);
      return;
    }
    setPhotoBusy(true);
    setPhotoError(null);
    try {
      setPhotoUrl(await loadPhotoUrl(report.photo_path));
    } catch (e) {
      setPhotoError(formatError(e));
    } finally {
      setPhotoBusy(false);
    }
  }

  return (
    <div className="run-report">
      <div className="run-report-head">
        <span className={`voucher-pill ${REPORT_STATUS_CLASS[report.status]}`}>
          {REPORT_STATUS_LABEL[report.status]}
        </span>
        <span className="muted">
          {report.reported_by_name ?? 'partner'} · {formatVenueDateTime(report.reported_at)}
        </span>
      </div>
      <div className="run-report-message">{report.message}</div>
      {report.photo_path ? (
        <div className="run-report-photo">
          <button type="button" className="btn-link" onClick={() => void togglePhoto()} disabled={photoBusy}>
            {photoBusy ? 'Foto laden…' : photoUrl ? 'Verberg foto' : 'Toon foto'}
          </button>
          {photoError ? <div className="error-card">{photoError}</div> : null}
          {photoUrl ? <img src={photoUrl} alt="Foto bij de melding" /> : null}
        </div>
      ) : null}
      {report.status !== 'open' && (report.review_note || report.reviewed_at) ? (
        <div className="run-report-review muted">
          Beoordeling
          {report.reviewed_by_name ? <> door {report.reviewed_by_name}</> : null}
          {report.reviewed_at ? <> · {formatVenueDateTime(report.reviewed_at)}</> : null}
          {report.review_note ? <>: {report.review_note}</> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}
